import {
  Badge,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
} from "@material-ui/core";
import Hidden from "@material-ui/core/Hidden";
import { useTheme } from "@material-ui/core/styles";
import SettingsIcon from "@material-ui/icons/Settings";
import WarningIcon from "@material-ui/icons/Warning";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Label,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getIntegerFromSpelledNumber,
  hslToHex,
  numberToWord,
} from "../Utils/Common";
import Title from "./Title";

function TooltipFragment(props) {
  return (
    <React.Fragment>
      {props?.temp > props?.alHigh || props?.temp < props?.alLow ? (
        <WarningIcon
          style={{
            width: "1em",
            height: "1em",
            fontSize: "1rem",
            color: "red",
          }}
        />
      ) : undefined}{" "}
      {props?.name} {props?.temp} °C <br />
    </React.Fragment>
  );
}

function tooltipContent(tooltipProps, deviceConfig, lines) {
  if (
    tooltipProps.payload === null ||
    tooltipProps.payload.length === 0 ||
    !tooltipProps.payload[0].payload
  ) {
    return undefined;
  }
  console.log(lines);

  const checkLines = (key) => {
    var v = "sensor" + getIntegerFromSpelledNumber(key.replace("temp", ""));
    if (lines) {
      if (lines[v] !== undefined) {
        return lines[v];
      }
    }
    return true;
  };

  const getDataWithKey = (key, target) => {
    var base =
      deviceConfig[
        "sensor" + getIntegerFromSpelledNumber(key.replace("temp", ""))
      ];
    if (base) {
      if (base[target]) {
        return base[target];
      }
    }
  };

  return (
    <React.Fragment>
      {tooltipProps.payload[0].payload.timeStamp} <br />
      {Object.keys(tooltipProps.payload[0].payload).map((key) =>
        key !== "timeStamp" &&
        tooltipProps.payload[0].payload[key] &&
        checkLines(key) ? (
          <TooltipFragment
            temp={tooltipProps.payload[0].payload[key]}
            alHigh={getDataWithKey(key, "alertHigh")}
            alLow={getDataWithKey(key, "alertLow")}
            name={getDataWithKey(key, "name") || getDataWithKey(key, "id")}
          />
        ) : null
      )}
    </React.Fragment>
  );
}

const getCheckboxLabel = (value, index) => {
  if (value && value.length < 5) {
    return value;
  }
  return index;
};

export default function TemperatureChart({ data }) {
  var {
    id,
    deviceConfig,
    lines,
    setLines,
    title,
    chartData,
    setChartCopies,
    chartCopies,
    copyData,
  } = data;
  const theme = useTheme();
  const { t } = useTranslation();
  const [innerState, setInnerState] = useState();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const func = (data) => {
    setInnerState(data);
  };

  const hasN = (data) => {
    if (data) {
      return data.toString().includes("n");
    }
    return false;
  };

  if (lines === undefined && setLines === undefined) {
    setLines = func;
    lines = innerState;
  }

  let handleChange = (event, t, refline, name) => {
    var o = {};
    if (lines) {
      if (lines[id]) {
        o = { ...lines[id] };
      }
    }
    var obj = {};
    if (refline) {
      obj = { ...o, [event.target.name]: event.target.value };
    } else {
      if (name) {
        if (o[name] !== undefined) {
          obj = {
            ...o,
            [name]: !o[name],
          };
        } else {
          obj = {
            ...o,
            [name]: false,
          };
        }
      } else {
        obj = {
          ...o,
          [event.target.name]: event.target.checked,
        };
      }
    }
    var ob = {};
    if (lines) {
      ob = { ...lines };
    }
    setLines({
      ...ob,
      [id]: { ...obj },
    });
  };

  const checkLines = (value, def) => {
    if (lines) {
      if (lines[id]) {
        if (lines[id][value] !== undefined) {
          return lines[id][value];
        }
      }
    }
    if (def) {
      return def;
    }
    return false;
  };

  return data ? (
    <React.Fragment>
      <Hidden xsDown>
        <Grid container>
          <Grid item xs={11}>
            <Title>{title}</Title>
          </Grid>
          <Grid item xs={1}>
            <IconButton
              color="inherit"
              aria-haspopup="true"
              onClick={handleClick}
            >
              <Badge>
                <SettingsIcon />
              </Badge>
            </IconButton>
          </Grid>
        </Grid>

        <Menu
          id="chart-settings-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {setChartCopies ? (
            hasN(id) ? (
              <MenuItem
                onClick={() =>
                  setChartCopies({ ...chartCopies, [copyData.id]: undefined })
                }
                variant="outlined"
              >
                {t("monitoring.remove")}
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() =>
                  setChartCopies({ ...chartCopies, [copyData.id]: 1 })
                }
                variant="outlined"
              >
                {t("monitoring.copy")}
              </MenuItem>
            )
          ) : null}
          <ListSubheader disableSticky={true}>
            {t("temperatureChart.sensors")}
          </ListSubheader>
          {Object.keys(deviceConfig).map((key, idx) =>
            key.includes("sensor") && deviceConfig[key] !== null ? (
              <MenuItem
                onClick={() => {
                  handleChange(undefined, undefined, false, key);
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name={key}
                      checked={checkLines(key, true)}
                      onChange={handleChange}
                      size="small"
                      color="primary"
                    />
                  }
                  label={getCheckboxLabel(
                    deviceConfig[key].name,
                    key.replace("sensor", "")
                  )}
                />
              </MenuItem>
            ) : null
          )}
          <ListSubheader disableSticky={true}>
            {t("temperatureChart.thresholds")}
          </ListSubheader>
          <MenuItem>
            <TextField
              label={t("temperatureChart.referenceHigh")}
              type="number"
              name="refHigh"
              defaultValue={checkLines(
                "refHigh",
                deviceConfig.sensor1?.alertHigh
              )}
              onChange={(e) => {
                handleChange(e, undefined, true);
              }}
            />
          </MenuItem>
          <MenuItem>
            <TextField
              label={t("temperatureChart.referenceLow")}
              name="refLow"
              type="number"
              defaultValue={checkLines(
                "refLow",
                deviceConfig.sensor1?.alertLow
              )}
              onChange={(e) => {
                handleChange(e, undefined, true);
              }}
            />
          </MenuItem>
        </Menu>
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{
              top: 16,
              right: 16,
              bottom: 0,
              left: 24,
            }}
          >
            <XAxis dataKey="timeStamp" stroke={theme.palette.text.secondary} />
            <YAxis
              stroke={theme.palette.text.secondary}
              domain={[
                (dataMin) => Math.round(dataMin - 3),
                (dataMax) => Math.round(dataMax + 3),
              ]}
            >
              <Label
                angle={270}
                position="left"
                style={{
                  textAnchor: "middle",
                  fill: theme.palette.text.primary,
                }}
              >
                {t("temperatureChart.temperature") + " (°C)"}
              </Label>
            </YAxis>
            <Tooltip
              isAnimationActive={false}
              content={(props) =>
                tooltipContent(
                  props,
                  deviceConfig,
                  lines && lines[id] ? lines[id] : undefined
                )
              }
              wrapperStyle={{
                padding: 10,
                borderRadius: 5,
                color: "white",
                backgroundColor: theme.palette.primary.main,
              }}
            />
            {[...Array(15)].map((x, i) =>
              checkLines("sensor" + (i + 1), true) ? (
                <Line
                  type="monotone"
                  dataKey={"temp" + numberToWord(i + 1)}
                  stroke={hslToHex((360 / 10) * i, 100, 30)}
                  dot={false}
                  connectNulls={true}
                />
              ) : null
            )}
            <ReferenceLine
              y={checkLines("refHigh", deviceConfig.sensor1?.alertHigh)}
              label="Max"
              stroke="red"
              strokeDasharray="3 3"
            />
            <ReferenceLine
              y={checkLines("refLow", deviceConfig.sensor1?.alertLow)}
              label="Min"
              stroke="blue"
              strokeDasharray="3 3"
            />
          </LineChart>
        </ResponsiveContainer>
      </Hidden>
      <Hidden smUp>
        <Title>{t("temperatureChart.landscape")}</Title>
      </Hidden>
    </React.Fragment>
  ) : null;
}
